import { useState, useCallback, useMemo, useEffect } from 'react';
import { AdsColumnDefinitionInput } from '../ads-table-column-config';

interface UseAdsGenericTableConfig {
  selectedColumns: AdsColumnDefinitionInput[];
  setSelectedColumns: React.Dispatch<React.SetStateAction<AdsColumnDefinitionInput[]>>;
}

export const useAdsGenericTable = ({ selectedColumns, setSelectedColumns }: UseAdsGenericTableConfig) => {
  const initialColumnOrder = useMemo(() => {
    return selectedColumns.map((col) => col.id);
  }, [selectedColumns]);

  const [columnOrder, setColumnOrder] = useState<string[]>(initialColumnOrder);

  // Update columnOrder when selectedColumns changes
  useEffect(() => {
    setColumnOrder(initialColumnOrder);
  }, [initialColumnOrder]);

  const handleExpandColumn = useCallback(
    (columnId: string) => {
      setSelectedColumns((prevColumns: AdsColumnDefinitionInput[]) => {
        return prevColumns.map(
          (col) => (col.id === columnId ? { ...col, isColumnExpanded: !col.isColumnExpanded } : col), // eslint-disable-line no-nested-ternary
        );
      });
    },
    [setSelectedColumns],
  );

  const handleDragEnd = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      // Update columnOrder
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const movedColumn = newOrder.splice(fromIndex, 1)[0];
        newOrder.splice(toIndex, 0, movedColumn);
        return newOrder;
      });

      // Update selectedColumns to maintain synchronization
      setSelectedColumns((prevColumns) => {
        const newColumns = [...prevColumns];
        const movedColumn = newColumns.splice(fromIndex, 1)[0];
        newColumns.splice(toIndex, 0, movedColumn);
        return newColumns;
      });
    },
    [setSelectedColumns],
  );

  return {
    tableProps: {
      columnOrder,
      handleDragEnd,
      selectedColumns,
      setSelectedColumns,
      handleExpandColumn,
    },
  };
};
